import { Coverage, DocumentReference, Patient } from '@medplum/fhirtypes'
import { logError } from '@/utils/errorUtils'
import { useCallback, useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import { LoadingOverlay, Tabs } from '@mantine/core'
import { useMiddlewareClient } from '@/hooks'
import { INSURANCE_CARD_SIDES } from '@/constants/insuranceConstants'
import type {
  InsuranceCardDocsState,
  InsuranceCardSide,
  InsuranceRank,
} from '@/types/insuranceTypes'
import {
  getInsuranceNameByIdx,
  getInsuranceRankByOrder,
  normalizeResolvedCoverageSlots,
  getSubscriberName,
  getSubscriberDob,
  getSubscriberMemberId,
  convertFromISODate,
} from '@/utils/coverageUtils'
import { buildInsuranceDocuments, getDocumentAttachmentUrl } from '@/utils/documentReferenceUtils'

type Props = {
  patient: Patient
}

type ResolvedCoveragesResult = {
  patientId: string
  mode: 'hg' | 'local-only'
  primary?: Coverage
  secondary?: Coverage
  tertiary?: Coverage
}

export function Insurances({ patient }: Props) {
  const [insurances, setInsurances] = useState<Coverage[]>([])
  const [insuranceCardDocs, setInsuranceCardDocs] = useState<InsuranceCardDocsState>({})
  const [isLoading, setIsLoading] = useState(true)

  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const middlewareClient = middleware as unknown as {
    get: <T = unknown>(url: string) => Promise<{ result: T }>
  }

  const loadInsurances = useCallback(
    async (context?: { disposed: boolean }) => {
      setIsLoading(true)
      try {
        const { result } = await middlewareClient.get<ResolvedCoveragesResult>(
          `/insurance/patient/${patient.id}/coverages`,
        )
        const ordered = normalizeResolvedCoverageSlots(result)

        if (!context?.disposed) {
          setInsurances(ordered)
        }

        const docs = await buildInsuranceDocuments(medplum, ordered, getInsuranceRankByOrder)
        if (!context?.disposed) {
          setInsuranceCardDocs(docs)
        }
      } catch (error) {
        logError('insurance', 'Failed to fetch insurance data', error)
        if (!context?.disposed) {
          setInsurances([])
          setInsuranceCardDocs({})
        }
      } finally {
        if (!context?.disposed) {
          setIsLoading(false)
        }
      }
    },
    [medplum, middlewareClient, patient.id],
  )

  useEffect(() => {
    const context = { disposed: false }
    loadInsurances(context)

    return () => {
      context.disposed = true
      setInsurances([])
      setInsuranceCardDocs({})
    }
  }, [loadInsurances])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    let disposed = false
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ patientId?: string; rank?: InsuranceRank }>
      if (disposed || customEvent.detail?.patientId !== patient.id) {
        return
      }
      loadInsurances().catch((error) =>
        logError('insurance', 'Failed to refresh insurances', error),
      )
    }

    window.addEventListener('insurance-card-uploaded', handler)
    return () => {
      disposed = true
      window.removeEventListener('insurance-card-uploaded', handler)
    }
  }, [loadInsurances, patient.id])

  const getPlan = (coverage: Coverage) => {
    return (
      coverage.class?.find((c) => c.type?.coding?.find((t) => t.code === 'plan'))?.value ||
      coverage.payor?.[0]?.display ||
      '-'
    )
  }

  const getGroup = (coverage: Coverage) => {
    return (
      coverage.class?.find((c) => c.type?.coding?.find((t) => t.code === 'group'))?.value || '-'
    )
  }

  const getPayerId = (coverage: Coverage) => {
    return coverage.payor?.[0]?.identifier?.value || '-'
  }

  const getPolicyNumber = (coverage: Coverage) => {
    const policy =
      coverage.identifier?.find((i) => !i.system)?.value || coverage.subscriberId || '-'
    return policy.length > 30 ? '-' : policy
  }

  const getSubscriberMemberIdFallback = (coverage: Coverage) => {
    return getSubscriberMemberId(coverage) || '-'
  }

  const sortedIns = [...insurances].sort((a, b) => (a.order || 0) - (b.order || 0))

  return (
    <div className="relative p-4" style={{ minHeight: isLoading ? 200 : undefined }}>
      <LoadingOverlay visible={isLoading} />
      <Tabs defaultValue={(sortedIns[0]?.order || 1).toString()}>
        <Tabs.List>
          {sortedIns.map((coverage, idx) => (
            <Tabs.Tab value={coverage.order?.toString() || '-'} key={idx}>
              {getInsuranceNameByIdx(coverage.order || 1)} Insurance
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <div className="mt-4">
          {sortedIns.map((insurance, idx) => {
            const rank = getInsuranceRankByOrder(insurance.order)
            const cardDocs = insuranceCardDocs[rank]
            const hasImages = Boolean(cardDocs?.front || cardDocs?.back)
            return (
              <Tabs.Panel key={idx} value={insurance.order?.toString() || '-'}>
                <div className="flex flex-col gap-4">
                  <div className="flex gap-8">
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-2">
                        <p className="text-light text-right text-sm">Plan</p>
                        <p className="text-light text-right text-sm">Type</p>
                        <p className="text-light text-right text-sm">Policy Number</p>
                        <p className="text-light text-right text-sm">Group Number</p>
                        <p className="text-light text-right text-sm">Payer ID</p>
                      </div>

                      <div className="flex flex-col gap-2">
                        <p className="text-base text-sm">{getPlan(insurance)}</p>
                        <p className="text-base text-sm">
                          {insurance.type?.text ||
                            insurance.relationship?.coding?.[0]?.display ||
                            '-'}
                        </p>
                        <p className="text-base text-sm">{getPolicyNumber(insurance)}</p>
                        <p className="text-base text-sm">{getGroup(insurance)}</p>
                        <p className="text-base text-sm">{getPayerId(insurance)}</p>
                      </div>
                    </div>

                    {insurance.relationship?.coding?.[0]?.code?.toLowerCase() !== 'self' && (
                      <div className="flex gap-2">
                        <div className="flex flex-col gap-2">
                          <p className="text-light text-left text-sm">Subscriber Name</p>
                          <p className="text-light text-left text-sm">Subscriber DOB</p>
                          <p className="text-light text-left text-sm">Subscriber Member ID</p>
                          <p className="text-light text-left text-sm">&nbsp;</p>
                        </div>

                        <div className="flex flex-col gap-2">
                          <p className="text-base text-sm">{getSubscriberName(insurance) || '-'}</p>
                          <p className="text-base text-sm">
                            {convertFromISODate(getSubscriberDob(insurance)) || '-'}
                          </p>
                          <p className="text-base text-sm">
                            {getSubscriberMemberIdFallback(insurance)}
                          </p>
                          <p className="text-base text-sm">&nbsp;</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {hasImages && (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {INSURANCE_CARD_SIDES.map((side) => (
                        <div
                          key={side}
                          className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {side === 'front' ? 'Front of card' : 'Back of card'}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {cardDocs?.[side]
                                  ? `Uploaded ${formatUploadDate(cardDocs[side])}`
                                  : 'Not uploaded yet'}
                              </p>
                            </div>
                            {cardDocs?.[side]?.content?.[0]?.attachment?.url && (
                              <a
                                href={cardDocs[side]?.content?.[0]?.attachment?.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs font-medium text-indigo-600 hover:text-indigo-500"
                              >
                                Open
                              </a>
                            )}
                          </div>
                          {renderInsuranceImage(side, cardDocs?.[side])}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Tabs.Panel>
            )
          })}
        </div>
      </Tabs>
    </div>
  )
}

const formatUploadDate = (doc: DocumentReference): string => {
  const date = doc.date || doc.meta?.lastUpdated
  return date
    ? new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : 'Unknown'
}

const renderInsuranceImage = (side: InsuranceCardSide, doc?: DocumentReference) => {
  const label = side === 'front' ? 'front' : 'back'

  if (!doc) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
        <p className="text-sm text-gray-500">No {label} image uploaded yet.</p>
      </div>
    )
  }

  const imageUrl = doc ? getDocumentAttachmentUrl(doc) : undefined

  if (!imageUrl) {
    return (
      <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-6 text-center">
        <p className="text-sm text-amber-700">Attachment unavailable.</p>
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
      <div className="flex aspect-video items-center justify-center bg-gray-100">
        <img
          src={imageUrl}
          alt={`${label} of insurance card`}
          className="h-full w-full object-contain"
          loading="lazy"
        />
      </div>
    </div>
  )
}
